import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  ChevronLeft, Loader2, Save, FileText, Upload, Eye, Palette
} from 'lucide-react';

const AdminPDFSettings = () => {
  const [settings, setSettings] = useState({
    company_name: '3D Print Bureau',
    tagline: 'Production 3D Printing',
    abn: '',
    email: 'hello@3dprintbureau.com.au',
    phone: '',
    address: 'Melbourne, VIC, Australia',
    primary_color: '#CCFF00',
    validity_days: 30,
    show_thumbnails: true,
    terms: '• Payment terms: 50% deposit, 50% on completion\n• Lead time: 3-5 business days for standard orders\n• Express options available\n• All prices in AUD, GST inclusive'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/pdf-settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pdf-settings`, settings, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('PDF settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/admin/pdf-settings/upload-logo`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
    setUploadingLogo(false);
    event.target.value = '';
  };

  const previewPDF = async () => {
    // Open a sample PDF preview in new tab
    toast.info('Preview feature - go to Quote Requests and click PDF icon on any quote to preview');
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-white/50 hover:text-white">
                <ChevronLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white">PDF Quote Settings</h1>
                <p className="text-white/50 text-sm">Customize how your PDF quotes look</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={previewPDF}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={saveSettings}
                disabled={saving}
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Company Details */}
            <div className="bg-[#111] border border-white/10 rounded-sm p-6">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#CCFF00]" />
                Company Details
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-white/70">Company Name</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => updateSetting('company_name', e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-white/70">Tagline</Label>
                  <Input
                    value={settings.tagline}
                    onChange={(e) => updateSetting('tagline', e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-white/70">ABN (Australian Business Number)</Label>
                  <Input
                    value={settings.abn || ''}
                    onChange={(e) => updateSetting('abn', e.target.value)}
                    placeholder="e.g., 12 345 678 901"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                  <p className="text-white/40 text-xs mt-1">Will appear on Quotes and Invoices</p>
                </div>
                
                <div>
                  <Label className="text-white/70">Email</Label>
                  <Input
                    value={settings.email}
                    onChange={(e) => updateSetting('email', e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-white/70">Phone</Label>
                  <Input
                    value={settings.phone}
                    onChange={(e) => updateSetting('phone', e.target.value)}
                    placeholder="+61 3 XXXX XXXX"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-white/70">Address</Label>
                  <Textarea
                    value={settings.address}
                    onChange={(e) => updateSetting('address', e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                    rows={2}
                  />
                </div>

                {/* Logo Upload */}
                <div className="pt-4 border-t border-white/10">
                  <Label className="text-white/70">Company Logo</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      ref={logoInputRef}
                      onChange={uploadLogo}
                      className="hidden"
                    />
                    <Button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload Logo
                    </Button>
                    <p className="text-white/30 text-xs mt-2">PNG or JPEG, max 2MB</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quote Settings */}
            <div className="bg-[#111] border border-white/10 rounded-sm p-6">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <Palette className="w-5 h-5 text-[#CCFF00]" />
                Quote Settings
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-white/70">Primary Color</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer bg-transparent"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => updateSetting('primary_color', e.target.value)}
                      className="bg-white/5 border-white/10 text-white font-mono flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white/70">Quote Validity (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={settings.validity_days}
                    onChange={(e) => updateSetting('validity_days', parseInt(e.target.value) || 30)}
                    className="bg-white/5 border-white/10 text-white mt-1 w-32"
                  />
                </div>

                <div className="flex items-center justify-between py-3">
                  <div>
                    <Label className="text-white">Show Part Thumbnails</Label>
                    <p className="text-white/50 text-xs">Display 3D preview images in PDF</p>
                  </div>
                  <Switch
                    checked={settings.show_thumbnails}
                    onCheckedChange={(checked) => updateSetting('show_thumbnails', checked)}
                  />
                </div>
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="bg-[#111] border border-white/10 rounded-sm p-6 lg:col-span-2">
              <h3 className="text-lg font-bold text-white mb-4">Terms & Conditions</h3>
              <p className="text-white/50 text-sm mb-4">These will appear at the bottom of every PDF quote. Use bullet points (•) for list items.</p>
              
              <Textarea
                value={settings.terms}
                onChange={(e) => updateSetting('terms', e.target.value)}
                className="bg-white/5 border-white/10 text-white min-h-[150px] font-mono text-sm"
                placeholder="• Payment terms: 50% deposit, 50% on completion&#10;• Lead time: 3-5 business days"
              />
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminPDFSettings;
